import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Paper, Typography,
} from '@mui/material';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import { fetchSubleaguesForSeason } from '../../../services/seasons';
import NewSubleagueDialog from './NewSubleagueDialog';

/** Haalt de namen op van de beheerders van de meegegeven veilingen. */
async function fetchAdminNames(adminIds) {
    const unique = [...new Set(adminIds.filter(Boolean))];
    if (unique.length === 0) return {};

    const names = {};
    // Firestore staat maximaal 30 waarden per 'in'-query toe.
    for (let i = 0; i < unique.length; i += 30) {
        const chunk = unique.slice(i, i + 30);
        const snapshot = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk)),
        );
        snapshot.docs.forEach((d) => {
            names[d.id] = d.data().name || d.data().email || d.id;
        });
    }
    return names;
}

/**
 * Alle subleagues die op dit seizoen spelen. "Openen" brengt je in het
 * subleaguebeheer van die competitie.
 */
function AdminSubleagues() {
    const { season, seasonId } = useSeason();
    const notify = useNotification();
    const [subleagues, setSubleagues] = useState([]);
    const [adminNames, setAdminNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await fetchSubleaguesForSeason(seasonId);
            setSubleagues(list);
            setAdminNames(await fetchAdminNames(list.map((a) => a.adminId)));
        } catch (err) {
            console.error('Kon subleagues niet ophalen:', err);
            notify.error('De subleagues konden niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [seasonId, notify]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <Box component={RouterLink} to="/admin" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                    Seizoenen
                </Box>
                {' › '}
                <Box component={RouterLink} to={`/admin/season/${seasonId}/dashboard`} sx={{ color: 'primary.main', textDecoration: 'none' }}>
                    {season.displayName}
                </Box>
                {' › Subleagues'}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4">Subleagues</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Elke subleague is een eigen competitie met een eigen beheerder, deelnemers en veiling —
                        allemaal spelend op de stamgegevens en kalender van dit seizoen.
                    </Typography>
                </Box>
                <Button variant="contained" onClick={() => setDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                    Subleague aanmaken
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : subleagues.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Er speelt nog geen enkele subleague op dit seizoen.
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {subleagues.map((auction) => (
                        <Paper key={auction.id} sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                                <Typography variant="h6" sx={{ minWidth: 0 }}>{auction.name}</Typography>
                                <Chip
                                    size="small"
                                    label={auction.status === 'Test' ? 'Test' : 'Live'}
                                    color={auction.status === 'Test' ? 'default' : 'success'}
                                    variant={auction.status === 'Test' ? 'outlined' : 'filled'}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {adminNames[auction.adminId] || 'onbekende beheerder'}
                                {' · '}
                                {(auction.participantUids || []).length} deelnemers
                                {' · '}
                                {auction.spelfase || 'Voorbereiding'}
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                                <Button size="small" component={RouterLink} to={`/auction/${auction.id}`}>
                                    Openen
                                </Button>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            <NewSubleagueDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                seasonId={seasonId}
                seasonName={season.displayName}
                onCreated={() => {
                    setDialogOpen(false);
                    load();
                }}
            />
        </Box>
    );
}

export default AdminSubleagues;
